import React, { forwardRef } from "react";

import _ from "lodash";

import { useTranslation } from "utils";

import { OrganicAndMineralFertilizersProps } from "models";

import { AlertBanner, Divider, Flex, Form, Icon, Link, Spin, Tag, Trans } from "components";

import { CustomKlimRadioGroupWithPayouts } from "pages/app/all-field-view/components/forms/fertilizers/CustomKlimRadioGroupWithPayouts";
import { OrganicOrMineralFertilizerForm } from "pages/app/all-field-view/components/forms/fertilizers/OrganicOrMineralFertilizerForm";
import { useFertilizerForm } from "pages/app/all-field-view/hooks/useFertilizerForm";
import { isRequired, isRequiredForSaving } from "pages/app/all-field-view/utils/getInputRequirementConfig";

export const AddEditOrganicAndMineralFertilizers = forwardRef<HTMLElement, OrganicAndMineralFertilizersProps>(
  (
    {
      currency,
      chosenFertilizerType,
      disabled,
      existingFertilizers,
      baselineYears,
      recommendedSeedingDate,
      year,
      requirementLevel,
      practiceConfigForBulk,
    }: OrganicAndMineralFertilizersProps,
    ref
  ) => {
    const { t } = useTranslation(["modules", "allFieldView"]);

    const {
      isFertilizerTypeApplied,
      fertilizerApplications,
      handleFertilizerTypeChange,
      handleRemoveFertilizerApplication,
      addNewFertilizerApplication,
      updateFertilizerApplication,
      isFertilizerLoading,
      fertilizers,
      toggleDisabled,
      hasMixedValues,
    } = useFertilizerForm(chosenFertilizerType, existingFertilizers, disabled, practiceConfigForBulk);

    const fertilizerApplicationWithYear = fertilizerApplications.find((fertilizer) => fertilizer.year !== undefined);

    const isBaseline = fertilizerApplicationWithYear?.year ? baselineYears?.includes(fertilizerApplicationWithYear.year) : baselineYears?.includes(year);

    return (
      <>
        <Flex vertical className="fertilizers" data-testid={`add-edit-${chosenFertilizerType === "organic" ? "organic" : "mineral"}-fertilizer-form`} ref={ref}>
          <div className="fertilizers-header-container">
            <div className="margin-bottom-100">
              <Flex gap="0.75rem">
                <h5 className="fertilizers-title"> {t(`allFieldView:fertilizerForm.mainTitle.${chosenFertilizerType}`)} </h5>
                {isBaseline && (
                  <Tag
                    dataTestId={`timeline-drawer-${chosenFertilizerType}-form-baseline-tag`}
                    className="info-tag-drawer"
                    colour="grey"
                    text={t("allFieldView:fertilizerForm.baseline")}
                  />
                )}
              </Flex>
            </div>

            <Flex className="row-space-between" justify="space-between" align="center" gap="1rem">
              <span className="paragraph-m">
                {t(`allFieldView:fertilizerForm.radioTitle.${chosenFertilizerType}`)} {isRequired(requirementLevel) && "*"}
              </span>

              <Form.Item
                name={`is${_.upperFirst(chosenFertilizerType)}Applied`}
                initialValue={isFertilizerTypeApplied}
                rules={[{ required: isRequiredForSaving(requirementLevel), message: t("allFieldView:fertilizerForm.fertilizerRequired") }]}
                help={hasMixedValues ? t("tillageForm.helperTextYesNoQuestion") : undefined}
              >
                <CustomKlimRadioGroupWithPayouts
                  value={isFertilizerTypeApplied}
                  currency={currency}
                  onChange={(e) => {
                    handleFertilizerTypeChange(e.target.value);
                  }}
                  dataTestId="fertilizer-radio-group"
                  className="fertilizers-radio"
                  disabled={toggleDisabled}
                />
              </Form.Item>
            </Flex>
            {practiceConfigForBulk?.showMessage && (
              <AlertBanner
                className="margin-top-200 margin-bottom-200"
                description={<Trans i18nKey={"allFieldView:cropForm.multiplePracticesMessage"} components={{ strong: <strong /> }} />}
                type="make-aware"
                iconName="tabler/info-circle"
                iconAlign="start"
              />
            )}
          </div>

          {isFertilizerTypeApplied && (
            <>
              <Divider />

              <div className="fertilizers-body-container">
                {isFertilizerLoading ? (
                  <Flex justify="center">
                    <Spin />
                  </Flex>
                ) : (
                  fertilizerApplications.map((application, index) => (
                    <OrganicOrMineralFertilizerForm
                      key={`${chosenFertilizerType}-fertilizer-${application.id}-${index}`}
                      index={index}
                      chosenFertilizerType={chosenFertilizerType}
                      fertilizers={fertilizers}
                      existingFertilizerApplication={application}
                      onRemove={() => handleRemoveFertilizerApplication(index)}
                      onUpdate={updateFertilizerApplication}
                      recommendedSeedingDate={recommendedSeedingDate}
                      currency={currency}
                    />
                  ))
                )}

                <Flex justify="flex-start" className="margin-top-150 margin-bottom-50">
                  <Link
                    dataTestId={`fertilizer-add-new-${chosenFertilizerType}`}
                    onClick={addNewFertilizerApplication}
                    className="fertilizers-add-new-btn"
                    disabled={disabled}
                  >
                    <Icon iconName="klim/plus" className="icon inline" /> {t(`allFieldView:fertilizerForm.add.${chosenFertilizerType}`)}
                  </Link>
                </Flex>
              </div>
            </>
          )}
        </Flex>
      </>
    );
  }
);
